import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPage() {
  const today = new Date().toLocaleDateString("es", {
    year: "numeric", month: "long", day: "numeric",
  });
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="privacy-page">
      <header className="border-b border-white/8 px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="font-display text-sm tracking-tighter text-white/80">
          PLAY YOUR GAME™
        </Link>
        <Link to="/" className="text-[10px] uppercase tracking-[0.3em] text-white/40 hover:text-white inline-flex items-center gap-2">
          <ArrowLeft className="w-3 h-3" /> Volver
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24 space-y-10 text-white/80 leading-relaxed">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#29BC9B] mb-4">Información legal</div>
          <h1 className="font-display text-4xl md:text-5xl text-white mb-3">Política de privacidad y protección de datos</h1>
          <p className="text-sm text-white/40">Última actualización: {today}</p>
        </div>

        <section>
          <h2 className="font-display text-2xl text-white mb-3">1. Responsable del tratamiento</h2>
          <p>
            PLAY YOUR GAME™ (en adelante, "la Plataforma") es la responsable del tratamiento de los datos personales que el usuario proporciona al completar el test de evolución estratégica en <a className="text-[#29BC9B] underline" href="https://play-your-game.com">play-your-game.com</a>.
          </p>
          <p className="mt-3">
            Para cualquier consulta relacionada con tus datos, escríbenos a <a className="text-[#29BC9B] underline" href="mailto:results@play-your-game.com">results@play-your-game.com</a>.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-white mb-3">2. Datos que recolectamos</h2>
          <ul className="space-y-2 list-disc pl-5">
            <li><span className="text-white">Nombre</span> e <span className="text-white">email</span> que el usuario ingresa al final del test.</li>
            <li><span className="text-white">Respuestas del test</span> y el <span className="text-white">arquetipo dominante y secundario</span> calculados.</li>
            <li><span className="text-white">Fecha y hora</span> de envío del test y del consentimiento.</li>
            <li>Datos técnicos básicos de la conexión (dirección IP, navegador), únicamente con fines de seguridad y prevención de fraude.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl text-white mb-3">3. Finalidad y base legal</h2>
          <p>Tratamos tus datos con las siguientes finalidades:</p>
          <ul className="space-y-2 list-disc pl-5 mt-3">
            <li><span className="text-white">Enviarte tu resultado por email</span> y permitirte visualizarlo en una página personalizada.</li>
            <li>Mejorar el contenido y la metodología del test (análisis estadísticos agregados, nunca individuales).</li>
            <li>Enviarte comunicaciones relacionadas con tu evolución estratégica, en caso de que aceptes recibirlas.</li>
          </ul>
          <p className="mt-3">La base legal del tratamiento es <span className="text-white">tu consentimiento expreso</span>, que otorgas al marcar la casilla "Acepto la política de privacidad" antes de iniciar el test.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-white mb-3">4. Conservación</h2>
          <p>Conservaremos tus datos mientras mantengas tu consentimiento o hasta que solicites su eliminación. Los datos técnicos de seguridad se conservan por un máximo de 12 meses.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-white mb-3">5. Destinatarios y terceros</h2>
          <p>Para operar la Plataforma utilizamos los siguientes encargados del tratamiento, todos con garantías de seguridad y privacidad:</p>
          <ul className="space-y-2 list-disc pl-5 mt-3">
            <li><span className="text-white">Resend</span> (envío de emails transaccionales).</li>
            <li><span className="text-white">MongoDB Atlas / Emergent</span> (almacenamiento seguro y hosting de la aplicación).</li>
          </ul>
          <p className="mt-3">No vendemos ni cedemos tus datos a terceros con fines comerciales.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-white mb-3">6. Tus derechos</h2>
          <p>Puedes ejercer en cualquier momento los siguientes derechos:</p>
          <ul className="space-y-2 list-disc pl-5 mt-3">
            <li><span className="text-white">Acceso</span>: conocer qué datos tuyos tenemos.</li>
            <li><span className="text-white">Rectificación</span>: corregir datos inexactos.</li>
            <li><span className="text-white">Supresión</span> ("derecho al olvido"): pedirnos que eliminemos tus datos.</li>
            <li><span className="text-white">Oposición</span> y <span className="text-white">limitación</span> del tratamiento.</li>
            <li><span className="text-white">Portabilidad</span>: recibir tus datos en un formato estructurado.</li>
            <li><span className="text-white">Retirar el consentimiento</span> en cualquier momento.</li>
          </ul>
          <p className="mt-3">Para ejercer cualquiera de estos derechos, envía un email a <a className="text-[#29BC9B] underline" href="mailto:results@play-your-game.com">results@play-your-game.com</a> con el asunto "Protección de datos" indicando el derecho que deseas ejercer y adjuntando un documento que acredite tu identidad.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-white mb-3">7. Seguridad</h2>
          <p>Aplicamos medidas técnicas y organizativas razonables (cifrado en tránsito mediante HTTPS, accesos restringidos, copias de seguridad) para proteger tus datos frente a accesos no autorizados, pérdida o alteración.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-white mb-3">8. Cambios en la política</h2>
          <p>Podemos actualizar esta política para reflejar cambios legales o de funcionamiento. Publicaremos siempre la fecha de la última actualización al inicio del documento.</p>
        </section>

        <section className="pt-6 border-t border-white/10">
          <p className="text-sm text-white/50 italic">
            Si tienes dudas sobre el tratamiento de tus datos en PLAY YOUR GAME™, escríbenos a <a className="text-[#29BC9B] underline" href="mailto:results@play-your-game.com">results@play-your-game.com</a>.
          </p>
        </section>
      </main>

      <footer className="border-t border-white/5 px-6 md:px-12 py-10 text-center text-[10px] uppercase tracking-[0.3em] text-white/30">
        © PLAY YOUR GAME™
      </footer>
    </div>
  );
}
